import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationExtras, QueryParamsHandling, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { QueryParams } from 'src/app/types';
import { ClientService } from '../client/client.service';

@Injectable({
  providedIn: 'root'
})
export class QueryParamsService {
  public qParams = signal<QueryParams>({});

  queryParams: any = {};
  queryParamsChange = new BehaviorSubject(this.queryParams);

  queryParamsChangeType = new BehaviorSubject(null);
  currentSharedQueryParamsChangeType = this.queryParamsChangeType.asObservable();
  
  queryParamsFilterKeyType = new BehaviorSubject(null);
  currentSharedQueryParamsFilterKeyType = this.queryParamsFilterKeyType.asObservable();

  sharedNavigationChangedState = new BehaviorSubject<any>(null);
  currentSharedNavigationChangedState = this.sharedNavigationChangedState.asObservable();

  updateSharedNavigationChangedState(sharedNavigatoionChangedState: any) {
    this.sharedNavigationChangedState.next(sharedNavigatoionChangedState);
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private client: ClientService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    const params = this.queryToObject(this.document.location.search);
    this.handleParamsChanges(params);
     this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
    ).subscribe(() => {
      const params = this.queryToObject(this.document.location.search);
      this.handleParamsChanges(params);
    });
    
  }

  private parseQueryParams(params: any): QueryParams {
    const queryParams = structuredClone(params);
    
    return queryParams;
  }

  public handleParamsChanges(params: Record<string, any>) {
    const defaultParams = this.extractDefaultParams();
    Object.keys(defaultParams).forEach((key) => {
      params[key] = defaultParams[key];
    });
    this.queryParams = JSON.parse(JSON.stringify(params));
    Object.keys(this.queryParams).forEach((key) => {
      if (!(this.queryParams[key] instanceof Array)) {
        this.queryParams[key] = [this.queryParams[key]]
      }
    });
    const parsedParams = this.parseQueryParams(params);
    this.qParams.set(parsedParams);
    this.queryParamsChange.next(this.queryParams);
  }

  public extractDefaultParams() {
    const pathName = this.document.location.pathname.split('/').slice(2).join('/');

    const path = this.client.client.listOfNavbarItems.find((item: any) => {
      const tmp = structuredClone(item);
      if (tmp?.filterString?.length) {
        tmp.link = tmp.link.replace('/for-sale/', '/category/');
      }
      return tmp.link === `/${pathName}`;
    });
    const filterString = path?.filterString || '';
    const filters = this.queryToObject(filterString);
    delete filters['pageIndex'];
    
    return filters;
  }

  public queryToObject(query: string) {
    const params = new URLSearchParams(query?.startsWith('?') ? query.slice(1) : query);
    const obj: Record<string, any> = {};

    for (const [key, value] of params) {
      if (obj[key] === undefined) {
        obj[key] = value;                 // first value
      }
      else if (obj[key]) {
        obj[key] = [obj[key], value].flat();
      }
      else if (Array.isArray(obj[key])) {
        obj[key].push(value);             // already an array → push
      }
      else {
        obj[key] = value;     // convert to array
      }
    }
    return obj;
  }

  setQueryParams(url: string[] = [], extras?: NavigationExtras | undefined) {
    const defaultParams = this.extractDefaultParams();
    const tmp = structuredClone(extras?.queryParams || {});

    Object.keys(defaultParams).forEach((key) => {
      if (tmp && tmp[key]) {
        delete tmp[key];
      }
    });
    
    return this.router.navigate(url, {
      queryParamsHandling: extras?.queryParamsHandling,
      relativeTo: extras?.relativeTo,
      queryParams: tmp,
      replaceUrl: extras?.replaceUrl,
      skipLocationChange: extras?.skipLocationChange,
      state: extras?.state,
      preserveFragment: extras?.preserveFragment,
      fragment: extras?.fragment,
    })
  }

  setQueryParamsChangeType(type: any) {
    this.queryParamsChangeType.next(type)
  }

  setQueryParamsFilterKeyType(type: any) {
    this.queryParamsFilterKeyType.next(type)
  }

  routeForInput(input: any, route: string | undefined) {
    const path = route ? [route] : undefined;
    return this.setQueryParams(path, {
      queryParamsHandling: "merge",
      relativeTo: this.activatedRoute,
      queryParams: input,
    })
  }

  setUniqueValueForKey(input: any, route: string | undefined) {
    if (!input['pageIndex'] && Object.keys(input).length > 0) {
      input['pageIndex'] = 0;
    }
    if ((input['pageIndex'] || input['pageIndex'] === 0) && input['pageSize']) {
      this.setQueryParams([], {
        relativeTo: this.activatedRoute,
        queryParams: { pageIndex: input['pageIndex'], pageSize: input['pageSize'] }
      });
    }
    return this.routeForInput(input, route);
  }

  clearFilters() {
    this.setQueryParams([], {
      relativeTo: this.activatedRoute,
      queryParams: this.extractDefaultParams(),
    })
  }
}
